import type { TError, TForwardResolution } from '@onesource/schemas';
import { queryOptions, useQuery } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';

export interface ResolveContentInput {
  program: string;
  module?: string;
  lesson?: string;
  item?: string;
}

export const getResolveContentQueryKey = (input: ResolveContentInput) => [
  ENTITY,
  'resolve',
  input.program,
  input.module ?? null,
  input.lesson ?? null,
  input.item ?? null,
];

/**
 * Query options for resolving a slug chain (program[/module[/lesson[/item]]]) to
 * entity ids. Shared by the hook and by the slug route loaders (via
 * `queryClient.ensureQueryData`). Slugs are stable, so cache generously.
 */
export const getResolveContentQueryOptions = (input: ResolveContentInput) =>
  queryOptions<TForwardResolution, TError>({
    queryKey: getResolveContentQueryKey(input),
    queryFn: async () => {
      const params = new URLSearchParams({ program: input.program });
      if (input.module) {
        params.set('module', input.module);
      }
      if (input.lesson) {
        params.set('lesson', input.lesson);
      }
      if (input.item) {
        params.set('item', input.item);
      }
      const res = await axios.get<TForwardResolution>(`/community-hub/resolve?${params.toString()}`);
      return res.data;
    },
    enabled: !!input.program,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

export const useResolveContent = (input: ResolveContentInput) => useQuery(getResolveContentQueryOptions(input));
